import { Report, ReportSubmission, ReportFilters, ReportCategory, ReportPaginationData } from '../types/report';

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api/v1';

class ReportService {
    private getAuthHeaders() {
        const token = localStorage.getItem('token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }

    async submitReport(reportData: ReportSubmission): Promise<{ success: boolean; data?: Report; message?: string; error?: string; errors?: any[] }> {
        try {
            const response = await fetch(`${API_BASE_URL}/reports`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(reportData)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Submit report error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to submit report'
            };
        }
    }

    async getMyReports(filters: ReportFilters = {}): Promise<{ success: boolean; data?: ReportPaginationData; error?: string }> {
        try {
            const queryParams = new URLSearchParams();

            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, String(value));
                }
            });

            const response = await fetch(`${API_BASE_URL}/reports/my-reports?${queryParams}`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Get my reports error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch reports'
            };
        }
    }

    async getReportDetails(reportId: string): Promise<{ success: boolean; data?: Report; error?: string }> {
        try {
            const response = await fetch(`${API_BASE_URL}/reports/${reportId}`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Get report details error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch report details'
            };
        }
    }

    async getReportCategories(): Promise<{ success: boolean; data?: ReportCategory[]; error?: string }> {
        try {
            const response = await fetch(`${API_BASE_URL}/reports/categories`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Get report categories error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch report categories'
            };
        }
    }

    async uploadEvidence(files: File[]): Promise<{ success: boolean; data?: string[]; error?: string }> {
        try {
            const formData = new FormData();
            files.forEach((file) => {
                formData.append(`screenshots`, file);
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/upload/images`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            return {
                success: true,
                data: data.data.urls
            };
        } catch (error) {
            console.error('Upload evidence error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to upload evidence'
            };
        }
    }
}

export const reportService = new ReportService();